import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '../config/queryClient';

export interface TenantData {
  id: string;
  nom: string;
  type: string;
  email: string | null;
  telephone: string | null;
  adresse: string | null;
  logo_url: string | null;
  couleurs_theme: { primaire: string; secondaire: string; accent: string } | null;
  sous_domaine: string | null;
  devise: string;
  actif: boolean;
  niu?: string | null;
  registre_commerce?: string | null;
  code_marchand_om?: string | null;
  code_marchand_momo?: string | null;
  plan_abonnement?: 'starter' | 'business' | 'agence_fret';
  abonnement_prix_mensuel?: number;
  abonnement_statut?: 'actif' | 'en_retard' | 'suspendu';
}

interface User {
  id: string;
  email: string | null;
  nom: string;
  role: string;
  tenant: TenantData | null;
  totp_enabled?: boolean;
  // Module Expéditeur (Particulier / Vendeur Social)
  telephone?: string;
  profil_type?: 'particulier' | 'vendeur_social' | null;
  boutique_nom?: string | null;
  ramassage_ville?: string | null;
  ramassage_quartier?: string | null;
  cod_reversement_moyen?: 'om' | 'momo' | 'cash' | null;
}

export interface LoginNotification {
  lastLoginAt: string | null;
  failedAttempts: number;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loginNotification: LoginNotification | null;
  /** For super_admin: UUID of impersonated tenant, '__all__' for global view, null = not selected */
  activeTenantId: string | null;
  activeTenantName: string | null;
  setAuth: (user: User, token: string, refreshToken?: string) => void;
  setTokens: (token: string, refreshToken: string) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  updateTenant: (data: Partial<TenantData>) => void;
  setLoginNotification: (n: LoginNotification) => void;
  clearLoginNotification: () => void;
  setActiveTenant: (id: string, name: string) => void;
  clearActiveTenant: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      loginNotification: null,
      activeTenantId: null,
      activeTenantName: null,

      setAuth: (user, token, refreshToken) => {
        localStorage.setItem('access_token', token);
        // Un nouveau compte se connecte peut-être dans le même onglet qu'une
        // session précédente (autre utilisateur, autre tenant) — sans ça, le
        // cache React Query servirait ses anciennes données avant le premier
        // refetch (ex. Settings > Entreprise affichant le tenant précédent).
        queryClient.clear();
        set({
          user,
          token,
          refreshToken: refreshToken ?? null,
          activeTenantId: null,
          activeTenantName: null,
        });
      },

      setTokens: (token, refreshToken) => {
        localStorage.setItem('access_token', token);
        set({ token, refreshToken });
      },

      logout: () => {
        localStorage.removeItem('access_token');
        queryClient.clear();
        set({
          user: null,
          token: null,
          refreshToken: null,
          loginNotification: null,
          activeTenantId: null,
          activeTenantName: null,
        });
      },

      isAuthenticated: () => {
        const { token, user } = get();
        return !!token && !!user;
      },

      updateTenant: (data) => {
        const { user } = get();
        if (!user) return;
        set({
          user: {
            ...user,
            tenant: user.tenant ? { ...user.tenant, ...data } : null,
          },
        });
      },

      setLoginNotification: (n) => set({ loginNotification: n }),
      clearLoginNotification: () => set({ loginNotification: null }),

      setActiveTenant: (id, name) => {
        // Même raison que setAuth/logout : Super Admin changeant de tenant
        // impersonné ne doit jamais voir les données du tenant précédent.
        queryClient.clear();
        set({ activeTenantId: id, activeTenantName: name });
      },
      clearActiveTenant: () =>
        set({ activeTenantId: null, activeTenantName: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        activeTenantId: state.activeTenantId,
        activeTenantName: state.activeTenantName,
      }),
      onRehydrateStorage: () => {
        return () => {};
      },
    }
  )
);
